import { Check, ExternalLink, RotateCcw, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  APPLICATION_FORMS,
  APPLICATION_FORM_LABELS,
  applicationForm,
  BANDO_ACTIVITY_LABELS,
  BANDO_APPLICANT_LABELS,
  type ApplicationForm,
  type BandoActivity,
  type BandoApplicant,
  type BandoStatus,
} from '@sse/schema/bando';
import { btnGhost, btnPrimary, btnSecondary, inputBaseCls } from '../components/ui';
import { ApiError } from '../lib/api';
import { formatStamp } from '../lib/format';
import { useSession } from '../lib/session';
import { BandiRequests } from './bandi-requests';

const TABS = [
  ['pending', 'Da approvare'],
  ['published', 'Pubblicati'],
  ['rejected', 'Scartati'],
] as const;

interface Bando {
  id: string;
  sourceId: string;
  url: string;
  officialUrl?: string;
  title: string;
  issuer: string;
  summary: string;
  kind?: 'fondi' | 'volontari';
  applicants: BandoApplicant[];
  activities: BandoActivity[];
  form?: ApplicationForm;
  opensAt?: string;
  deadline?: string;
  maxAmount?: number;
  recurring?: { months: number[] };
  status: BandoStatus;
  updatedAt: string;
}

/**
 * Bandi e contributi per chi organizza (docs/07-admin-e-bot.md, "Bandi"): quelli delle fonti
 * da approvare (le fonti istituzionali si pubblicano da sole), i pubblicati e gli scartati. Gli
 * stessi pulsanti arrivano su Telegram con l'avviso del bando nuovo. Su ogni bando si sceglie il
 * tipo di domanda (evento, impresa, progetto): sono i campi di "Chiedi a noi la domanda" sul sito.
 */
export function BandiPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'requests' ? 'requests' : 'bandi';
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-2" role="tablist">
        {(
          [
            ['bandi', 'Bandi'],
            ['requests', 'Domande da preparare'],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            onClick={() => setParams(k === 'requests' ? { tab: k } : {})}
            className={tab === k ? btnPrimary : btnSecondary}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === 'requests' ? (
        <>
          <p className="max-w-3xl text-sm text-fg/75">
            Le richieste di chi organizza di preparare la domanda, già controllate dall’AI
            (ammissibili). Aggiorna lo stato (chi organizza lo vede nella pagina del bando) e
            prepara la bozza con l’AI.
          </p>
          <BandiRequests focus={params.get('id')} />
        </>
      ) : (
        <BandiList />
      )}
    </div>
  );
}

function BandiList() {
  const { api } = useSession();
  const [status, setStatus] = useState<BandoStatus>('pending');
  const [items, setItems] = useState<Bando[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    try {
      setItems((await api<{ items: Bando[] }>(`/bandi?status=${status}`)).items);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e));
    }
  };
  useEffect(() => {
    setItems(null);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const decide = async (id: string, next: BandoStatus) => {
    try {
      await api(`/bandi/${id}`, { method: 'PATCH', body: { status: next } });
      setItems((xs) => (xs ? xs.filter((b) => b.id !== id) : xs));
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e));
    }
  };

  const setForm = async (id: string, form: ApplicationForm) => {
    try {
      const b = await api<Bando>(`/bandi/${id}`, { method: 'PATCH', body: { form } });
      setItems((xs) => (xs ? xs.map((x) => (x.id === id ? { ...x, form: b.form } : x)) : xs));
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <h1 className="font-display text-h2">Bandi e contributi</h1>
      <p className="max-w-3xl text-sm text-fg/75">
        Letti ogni giorno dalle fonti (sorgenti «bandi» in Sorgenti) con l&apos;AI. Quelli della
        Regione e dei ministeri si pubblicano da soli; gli altri aspettano qui (e su Telegram).
      </p>
      <div className="flex flex-wrap gap-2" role="tablist">
        {TABS.map(([k, label]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={status === k}
            onClick={() => setStatus(k)}
            className={status === k ? btnPrimary : btnSecondary}
          >
            {label}
          </button>
        ))}
      </div>
      {error && <p className="bg-danger-soft p-3 text-sm">{error}</p>}
      {items === null ? (
        <p className="text-sm text-muted">Carico…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted">Nessun bando.</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {items.map((b) => (
            <li key={b.id} className="flex flex-col gap-2 border border-fg/15 bg-page p-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm text-fg/70">
                  {b.issuer} · {b.sourceId}
                </span>
                <span className="text-sm">
                  {b.recurring
                    ? `ricorrente (mesi ${b.recurring.months.join(', ')})`
                    : b.deadline
                      ? `scade il ${b.deadline}`
                      : 'senza scadenza'}
                </span>
              </div>
              <p className="font-medium">{b.title}</p>
              <p className="text-sm">{b.summary}</p>
              <p className="text-xs text-fg/70">
                {b.kind === 'volontari' ? 'Volontari · ' : ''}
                {b.applicants.map((a) => BANDO_APPLICANT_LABELS[a]).join(', ')} ·{' '}
                {b.activities.length
                  ? b.activities.map((a) => BANDO_ACTIVITY_LABELS[a]).join(', ')
                  : 'tutte le attività'}
                {b.maxAmount ? ` · fino a ${b.maxAmount} €` : ''} · aggiornato{' '}
                {formatStamp(b.updatedAt)}
              </p>
              {b.kind !== 'volontari' && (
                <label className="flex flex-wrap items-center gap-2 text-sm">
                  Domanda per
                  <select
                    value={applicationForm(b)}
                    onChange={(e) => void setForm(b.id, e.target.value as ApplicationForm)}
                    className={inputBaseCls}
                  >
                    {APPLICATION_FORMS.map((f) => (
                      <option key={f} value={f}>
                        {APPLICATION_FORM_LABELS[f]}
                      </option>
                    ))}
                  </select>
                  {!b.form && <span className="text-fg/60">(dedotto da chi e cosa)</span>}
                </label>
              )}
              <div className="flex flex-wrap items-center gap-2">
                {status !== 'published' && (
                  <button
                    type="button"
                    className={btnPrimary}
                    onClick={() => void decide(b.id, 'published')}
                  >
                    <Check className="size-4" aria-hidden /> Pubblica
                  </button>
                )}
                {status !== 'rejected' && (
                  <button
                    type="button"
                    className={btnSecondary}
                    onClick={() => void decide(b.id, 'rejected')}
                  >
                    <X className="size-4" aria-hidden /> Scarta
                  </button>
                )}
                {status === 'rejected' && (
                  <button
                    type="button"
                    className={btnGhost}
                    onClick={() => void decide(b.id, 'pending')}
                  >
                    <RotateCcw className="size-4" aria-hidden /> Rimetti da approvare
                  </button>
                )}
                <a
                  href={b.officialUrl ?? b.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={btnGhost}
                >
                  <ExternalLink className="size-4" aria-hidden /> Avviso
                </a>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
